
import React, { createContext, useContext, useState } from 'react';
import esTranslations from '@/i18n/es.json';
import enTranslations from '@/i18n/en.json';

export type Language = 'es' | 'en';

const translations: Record<Language, Record<string, any>> = {
  es: esTranslations,
  en: enTranslations,
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage as Language) || 'es';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): any => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    
    if (typeof value === 'string' || Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      return value;
    }
    
    // Fallback to Spanish
    if (language !== 'es') {
      let fallback: any = translations['es'];
      for (const k of keys) {
        fallback = fallback?.[k];
        if (fallback === undefined) break;
      }
      if (typeof fallback === 'string' || Array.isArray(fallback) || (typeof fallback === 'object' && fallback !== null)) return fallback;
    }
    
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
